import { useEffect, useState, useCallback, useMemo } from "react";
import { useMsal, useMsalAuthentication, useIsAuthenticated } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { ERRORS } from "../constants/errorMessages";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export function useAuthStatus() {
  const CONFIG = {
    graphUrl: "https://graph.microsoft.com/v1.0/me/checkMemberGroups"
  }
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const request = useMemo(() => ({ scopes: ["User.Read"] }), []);
  const { login, error } = useMsalAuthentication(InteractionType.Silent, request);

  const [authError, setAuthError] = useState<string | null>(null);
  const [isGroupMember, setIsGroupMember] = useState<boolean | null>(null);
  const [apiToken, setApiToken] = useState<string | null>(null);

  const checkGroupMembership = useCallback(async (account: any) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({ ...request, account });
      const header = new Headers();
      header.set("Content-Type", "application/json");
      header.append("Authorization", `Bearer ${tokenResponse.accessToken}`);

      const checkGroupId = process.env.REACT_APP_AUTHORIZED_GROUP_ID as string;

      const opts: RequestInit = {
        method: "POST",
        headers: header,
        body: JSON.stringify({ groupIds: [checkGroupId] }),
      };

      const graphResponse = await fetch(CONFIG.graphUrl, opts);
      const data = await graphResponse.json();

      setIsGroupMember(data.value?.length > 0);
    } catch (err) {
      console.error(ERRORS.noGroupMembership + ": ", err);
      setAuthError(ERRORS.noGroupMembership);
    }
  }, [CONFIG.graphUrl, instance, request]);

  useEffect(() => {
    if (error) {
      //login popup needed for Aka Projekt 
      const akaProjektOrigin = process.env.REACT_APP_AKA_PROJEKT_ORIGIN as string;
      let params = new URL(window.location.href).searchParams;
      let origin = params.get('origin');

      if (origin?.includes(akaProjektOrigin)) {
        login(InteractionType.Popup, request);
      } else {
        //save current path
        const redirectPath = window.location.pathname + window.location.search + window.location.hash;
        //base64 encode for stability all the way through
        const state = btoa(JSON.stringify({ redirectPath }));

        instance.loginRedirect({
          ...request, state,
          redirectStartPage: window.location.href
        }).catch((e) => console.error(ERRORS.loginRedirectFail, e));
        return;
      }
    }

    if (isAuthenticated) {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        instance.setActiveAccount(allAccounts[0]);
        checkGroupMembership(allAccounts[0]);
      }
    }
  }, [checkGroupMembership, error, instance, isAuthenticated, login, request]);

  const fetchApiToken = async () => {
    try {
      const scope = process.env.REACT_APP_SCOPE as string;
      const active = instance.getActiveAccount() ?? accounts[0];
      const tokenResponse = await instance.acquireTokenSilent({
        account: active,
        scopes: [scope],
      });
      setApiToken(tokenResponse.accessToken);
    } catch (err: any) {

      if (err instanceof InteractionRequiredAuthError) {
        // Kick off interactive consent for API scope
        await instance.acquireTokenRedirect({
          scopes: [process.env.REACT_APP_SCOPE as string],
        });
        return;
      }

      console.error(ERRORS.noTokenFetched, err);
      setAuthError(ERRORS.noTokenFetched);
    }
  };

  const accessApi = async () => {
    await fetchApiToken();
  };

  return {
    isAuthenticated,
    isGroupMember,
    apiToken,
    accessApi,
    authError
  };
}
